import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getProjects } from '../services/api'

// Gradient colors for project cards
const gradientColors = [
  { border: 'rgba(59, 130, 246, 0.3)', bg: 'rgba(59, 130, 246, 0.15)', icon: '#60a5fa' },
  { border: 'rgba(139, 92, 246, 0.3)', bg: 'rgba(139, 92, 246, 0.15)', icon: '#a78bfa' },
  { border: 'rgba(34, 211, 238, 0.3)', bg: 'rgba(34, 211, 238, 0.15)', icon: '#22d3ee' },
  { border: 'rgba(244, 114, 182, 0.3)', bg: 'rgba(244, 114, 182, 0.15)', icon: '#f472b6' },
  { border: 'rgba(52, 211, 153, 0.3)', bg: 'rgba(52, 211, 153, 0.15)', icon: '#34d399' },
]

function getGradientColor(index: number) {
  return gradientColors[index % gradientColors.length]
}

// Frosted glass panel styles
const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
  boxShadow: '0 0 40px rgba(59, 130, 246, 0.05)'
}

export default function Dashboard() {
  const { data: projects, isLoading, error } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects().then((res) => res.data),
  })

  const projectList = Array.isArray(projects) ? projects : []
  const totalRecordings = projectList.reduce((sum, p) => sum + (p.recording_count || 0), 0)
  const totalDocuments = projectList.reduce((sum, p) => sum + (p.document_count || 0), 0)

  if (error) {
    console.error('Projects fetch error:', error)
  }
  if (projects && !Array.isArray(projects)) {
    console.error('Projects is not an array:', projects)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(59, 130, 246, 0.1)',
              border: '2px solid rgba(59, 130, 246, 0.3)',
              boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)'
            }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading dashboard...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div
        className="rounded-2xl p-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(139, 92, 246, 0.15) 50%, rgba(6, 182, 212, 0.1) 100%)',
          border: '1px solid rgba(100, 180, 255, 0.2)',
          boxShadow: '0 0 60px rgba(59, 130, 246, 0.1)'
        }}
      >
        {/* Decorative elements */}
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full -mr-48 -mt-48"
          style={{ background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)' }}
        ></div>
        <div
          className="absolute bottom-0 left-0 w-64 h-64 rounded-full -ml-32 -mb-32"
          style={{ background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)' }}
        ></div>

        <div className="relative z-10">
          <h1
            className="text-3xl font-bold mb-2"
            style={{
              background: 'linear-gradient(135deg, #e2e8f0 0%, #94a3b8 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            Welcome to BreezyDoc
          </h1>
          <p style={{ color: 'rgba(148, 163, 184, 0.9)' }} className="text-lg">
            Your documentation automation assistant powered by AI
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Projects Card */}
        <Link
          to="/projects"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
          style={{
            ...glassPanel,
            borderColor: 'rgba(59, 130, 246, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(59, 130, 246, 0.15)',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-folder text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1"
              style={{
                background: 'rgba(59, 130, 246, 0.15)',
                color: '#60a5fa',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}
            >
              Projects
              <i className="fa-solid fa-arrow-right text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Total Projects</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{projectList.length}</p>
        </Link>

        {/* Recordings Card */}
        <Link
          to="/recordings"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
          style={{
            ...glassPanel,
            borderColor: 'rgba(52, 211, 153, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(52, 211, 153, 0.15)',
                border: '1px solid rgba(52, 211, 153, 0.3)'
              }}
            >
              <i className="fa-solid fa-microphone text-xl" style={{ color: '#34d399' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1"
              style={{
                background: 'rgba(52, 211, 153, 0.15)',
                color: '#34d399',
                border: '1px solid rgba(52, 211, 153, 0.3)'
              }}
            >
              Transcripts & Audio
              <i className="fa-solid fa-arrow-right text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Total Recordings</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{totalRecordings}</p>
        </Link>

        {/* Documents Card */}
        <Link
          to="/documents"
          className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.02] cursor-pointer group"
          style={{
            ...glassPanel,
            borderColor: 'rgba(139, 92, 246, 0.2)'
          }}
        >
          <div className="flex items-center justify-between mb-4">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-file-alt text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
            <span
              className="text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1"
              style={{
                background: 'rgba(139, 92, 246, 0.15)',
                color: '#a78bfa',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              Documents
              <i className="fa-solid fa-arrow-right text-xs opacity-0 group-hover:opacity-100 transition-opacity"></i>
            </span>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Documents Generated</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{totalDocuments}</p>
        </Link>
      </div>

      {/* Recent Projects */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold" style={{ color: '#e2e8f0' }}>Recent Projects</h2>
          <Link
            to="/projects"
            className="text-sm font-medium flex items-center transition-all duration-200 hover:gap-3 gap-2"
            style={{ color: '#60a5fa' }}
          >
            View all
            <i className="fa-solid fa-arrow-right"></i>
          </Link>
        </div>

        {projectList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {projectList.slice(0, 6).map((project, index) => {
              const colors = getGradientColor(index)
              return (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="block p-4 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
                  style={{
                    background: 'rgba(30, 41, 59, 0.5)',
                    border: `1px solid ${colors.border}`,
                  }}
                >
                  <div className="flex items-start space-x-4">
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{
                        background: colors.bg,
                        border: `1px solid ${colors.border}`
                      }}
                    >
                      <i className="fa-solid fa-folder text-xl" style={{ color: colors.icon }}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-semibold transition truncate"
                        style={{ color: '#e2e8f0' }}
                      >
                        {project.project_name}
                      </h3>
                      <p className="text-sm truncate" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        {project.customer_name}
                      </p>
                      <div className="flex items-center space-x-3 mt-2 text-xs" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        <span className="flex items-center">
                          <i className="fa-solid fa-microphone mr-1" style={{ color: '#a78bfa' }}></i>
                          {project.recording_count}
                        </span>
                        <span className="flex items-center">
                          <i className="fa-solid fa-file-alt mr-1" style={{ color: '#60a5fa' }}></i>
                          {project.document_count}
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              )
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <div
              className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
              style={{
                background: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}
            >
              <i className="fa-solid fa-folder-open text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>No projects yet</h3>
            <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Create your first project to get started</p>
            <Link
              to="/projects"
              className="px-6 py-3 rounded-xl text-sm font-medium inline-flex items-center space-x-2 transition-all duration-200 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                color: '#e2e8f0'
              }}
            >
              <i className="fa-solid fa-plus"></i>
              <span>Create Project</span>
            </Link>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="rounded-xl p-6" style={glassPanel}>
        <h2 className="text-lg font-bold mb-6" style={{ color: '#e2e8f0' }}>Quick Start Guide</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Link
            to="/projects"
            className="p-6 rounded-xl transition-all duration-300 hover:scale-[1.02] group"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{
                background: 'rgba(59, 130, 246, 0.15)',
                border: '1px solid rgba(59, 130, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-folder-plus text-2xl" style={{ color: '#60a5fa' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>1. Create Project</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Start a new project and organize your workshops
            </p>
          </Link>

          <div
            className="p-6 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(52, 211, 153, 0.2)'
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{
                background: 'rgba(52, 211, 153, 0.15)',
                border: '1px solid rgba(52, 211, 153, 0.3)'
              }}
            >
              <i className="fa-solid fa-microphone text-2xl" style={{ color: '#34d399' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>2. Upload Recordings</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Add your workshop recordings for AI transcription
            </p>
          </div>

          <div
            className="p-6 rounded-xl"
            style={{
              background: 'rgba(30, 41, 59, 0.5)',
              border: '1px solid rgba(139, 92, 246, 0.2)'
            }}
          >
            <div
              className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
              style={{
                background: 'rgba(139, 92, 246, 0.15)',
                border: '1px solid rgba(139, 92, 246, 0.3)'
              }}
            >
              <i className="fa-solid fa-file-alt text-2xl" style={{ color: '#a78bfa' }}></i>
            </div>
            <h3 className="font-semibold mb-1" style={{ color: '#e2e8f0' }}>3. Generate Documents</h3>
            <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
              Create comprehensive documentation with AI
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
